/**
 * The Call Viper mark.
 *
 * A telephone handset on a gradient tile. Chosen over the more literal
 * viper concepts because the sidebar renders this at 22px, and at that size
 * only the silhouette survives — a handset still reads as a handset, where a
 * snake's head, eye and tongue collapse into an ambiguous blob. An icon in a
 * dialer should say "calls" instantly and say nothing else.
 *
 * The glyph is drawn on a 24-unit grid and scaled into the 64-unit tile, which
 * keeps its stroke weights on whole units and the curves crisp at small sizes.
 *
 * To change the mark, edit both this component and `public/favicon.svg` — the
 * shape is defined in exactly those two places and they must stay in step.
 */
export function Logo({
  size = 26,
  variant = 'badge',
}: {
  size?: number
  /** `badge` draws the gradient tile behind the glyph; `glyph` draws it alone. */
  variant?: 'badge' | 'glyph'
}) {
  // Unique per instance: two gradients sharing an id on one page make the
  // second element inherit the first one's stops.
  const gid = `cv-grad-${size}-${variant}`

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      className="cv-logo"
      role="img"
      aria-label="Call Viper"
    >
      <defs>
        <linearGradient id={gid} x1="4" y1="4" x2="60" y2="60" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ff4d8d" />
          <stop offset="0.55" stopColor="#ff2d78" />
          <stop offset="1" stopColor="#7b2dff" />
        </linearGradient>
      </defs>

      {variant === 'badge' && <rect width="64" height="64" rx="15" fill={`url(#${gid})`} />}

      <g transform="translate(8,8) scale(2)">
        <path
          fill={variant === 'badge' ? '#ffffff' : `url(#${gid})`}
          d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24
             1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17
             0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
        />
      </g>
    </svg>
  )
}
